import { useRef, useState, type ClipboardEvent } from 'react';
import styled from 'styled-components';

import IconCloseSvg from '../../assets/icons/icon-close.svg?react';
import type { CountryCode } from '../config';
import { zIndexMap } from '../config';
import { useClickOutside } from '../hooks/use-click-outside';

import { BaseButton, BaseMaskedInput } from './base-primitives.styles';

type PhoneEntryOption = {
  flag: string;
  value: CountryCode;
};

type PhoneEntryFieldProps = {
  clearPhoneAriaLabel: string;
  countryCode: CountryCode;
  countryCodeAriaLabel: string;
  countryFlag: string;
  countryOptions: ReadonlyArray<PhoneEntryOption>;
  dataUi: string;
  onClearPhone: () => void;
  onPhoneChange: (value: string) => void;
  onSelectCountryCode: (country: CountryCode) => void;
  phoneAriaLabel: string;
  phoneMask: string;
  phoneValue: string;
};

const PhoneEntryPanel = styled.div.attrs<{ $dataUi: string }>(
  ({ $dataUi }) => ({
    'data-ui': $dataUi,
  }),
)`
  width: ${({ theme }) => theme.scale.px(331)};
  min-height: ${({ theme }) => theme.scale.px(57)};
  align-self: center;
  position: relative;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: ${({ theme }) =>
    `${theme.scale.px(96)} minmax(0, 1fr) ${theme.scale.px(14)}`};
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(12)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  padding-right: ${({ theme }) => theme.scale.px(22)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  transition: background-color 160ms ease, box-shadow 160ms ease;

  &:focus-within {
    background: ${({ theme }) => theme.colors.surface.overlay};
    box-shadow: 0 0 0 ${({ theme }) => theme.scale.px(2)}
      ${({ theme }) => theme.colors.brand.primary};
  }
`;

const CountryCodeSelectorContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const CountryCodeSelector = styled(BaseButton)`
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.overlay};
  z-index: ${zIndexMap.phoneEntry.countrySelector};
  display: grid;
  grid-template-columns: auto auto;
  justify-content: start;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(6)};
  padding: 0 ${({ theme }) => theme.scale.px(15)};
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 400;
  text-align: left;
`;

const CountryFlagIcon = styled.img`
  width: ${({ theme }) => theme.scale.px(29)};
  height: ${({ theme }) => theme.scale.px(20)};
  object-fit: cover;
  border-radius: ${({ theme }) => theme.scale.px(2)};
  pointer-events: none;
`;

const CountryCodeMenu = styled.div`
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: ${({ theme }) => theme.scale.px(3)};
  width: 100%;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  padding: ${({ theme }) => theme.scale.px(2)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(3)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  z-index: ${zIndexMap.phoneEntry.countryMenu};
`;

const CountryCodeOption = styled(BaseButton)<{ $isSelected: boolean }>`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(54)};
  border-radius: ${({ theme }) => theme.scale.px(10)};
  border: ${({ theme }) => theme.scale.px(2)} solid transparent;
  padding: ${({ theme }) => theme.scale.px(16)}
    ${({ theme }) => theme.scale.px(12)};
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(8)};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.brand.secondary : 'transparent'};
  background-clip: padding-box;
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 400;
  text-align: left;
  cursor: ${({ $isSelected }) => ($isSelected ? 'default' : 'pointer')};
`;

const PhoneInputField = styled(BaseMaskedInput)`
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.scale.px(16)};
  font-weight: 500;

  &:focus-visible {
    outline: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const ClearPhoneAction = styled(BaseButton)`
  width: ${({ theme }) => theme.scale.px(14)};
  height: ${({ theme }) => theme.scale.px(14)};
  box-sizing: border-box;
  display: grid;
  place-items: center;
  z-index: ${zIndexMap.phoneEntry.clearAction};
`;

const ClearPhoneIcon = styled(IconCloseSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  color: ${({ theme }) => theme.colors.text.primary};

  & path {
    stroke: currentColor;
  }
`;

export function PhoneEntryField({
  clearPhoneAriaLabel,
  countryCode,
  countryCodeAriaLabel,
  countryFlag,
  countryOptions,
  dataUi,
  onClearPhone,
  onPhoneChange,
  onSelectCountryCode,
  phoneAriaLabel,
  phoneMask,
  phoneValue,
}: PhoneEntryFieldProps) {
  const [isCountryMenuOpen, setCountryMenuOpen] = useState(false);
  const countryCodeSelectorRef = useRef<HTMLDivElement>(null);

  useClickOutside({
    enabled: isCountryMenuOpen,
    onOutsideClick: () => setCountryMenuOpen(false),
    refs: [countryCodeSelectorRef],
  });

  const toggleCountryMenu = () => {
    if (countryOptions.length <= 1) {
      return;
    }

    setCountryMenuOpen(value => !value);
  };

  const handleCountrySelect = (nextCountryCode: CountryCode) => {
    onSelectCountryCode(nextCountryCode);
    setCountryMenuOpen(false);
  };

  const handlePhonePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedPhone = event.clipboardData.getData('text');

    if (pastedPhone.length === 0) {
      return;
    }

    event.preventDefault();
    onPhoneChange(pastedPhone);
  };

  return (
    <PhoneEntryPanel $dataUi={dataUi}>
      <CountryCodeSelectorContainer ref={countryCodeSelectorRef}>
        <CountryCodeSelector
          aria-label={countryCodeAriaLabel}
          disabled={countryOptions.length <= 1}
          type="button"
          onClick={toggleCountryMenu}
        >
          <CountryFlagIcon src={countryFlag} alt="" />
          {countryCode}
        </CountryCodeSelector>

        {countryOptions.length > 1 && isCountryMenuOpen ? (
          <CountryCodeMenu>
            {countryOptions.map(option => (
              <CountryCodeOption
                $isSelected={option.value === countryCode}
                key={option.value}
                type="button"
                onClick={() => handleCountrySelect(option.value)}
              >
                <CountryFlagIcon src={option.flag} alt="" />
                {option.value}
              </CountryCodeOption>
            ))}
          </CountryCodeMenu>
        ) : null}
      </CountryCodeSelectorContainer>
      <PhoneInputField
        aria-label={phoneAriaLabel}
        mask={phoneMask}
        placeholder={phoneMask}
        value={phoneValue}
        onAccept={value => onPhoneChange(String(value))}
        onPaste={handlePhonePaste}
      />
      <ClearPhoneAction
        aria-label={clearPhoneAriaLabel}
        type="button"
        onClick={onClearPhone}
      >
        <ClearPhoneIcon />
      </ClearPhoneAction>
    </PhoneEntryPanel>
  );
}
